import * as React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Card,
  CardActionArea,
  Chip,
  Divider,
  Stack,
  Typography
} from "@mui/material";
import { Check as CheckIcon } from "@mui/icons-material";
import { WasPrice, formatRupees } from './billzappPlans.jsx';

// One "Pocket Billing Machine With Software" bundle: a printer plus the first year of a plan.
// Bundles are sold with yearly software only. The whole card is a link to the bundle's page,
// where it is ordered.
export default function BillingMachineCard({ bundle }) {
  const { printer, plan, software } = bundle;
  const planPaise = plan.annual.amountPaise;
  // On sale like the plan cards: the software year's "was" price, and the total with it.
  const planWasPaise = plan.annual.wasAmountPaise;

  return (
    <Card
      elevation={plan.recommended ? 8 : 4}
      sx={{
        height: '100%',
        position: 'relative',
        overflow: 'visible',
        border: '2px solid',
        borderColor: plan.recommended ? 'primary.main' : 'transparent',
        transition: 'all 0.3s ease',
        '&:hover': {
          borderColor: 'primary.main',
          transform: 'translateY(-5px)',
          boxShadow: 12
        }
      }}
    >
      {plan.recommended && (
        <Chip
          label="RECOMMENDED"
          color="primary"
          size="small"
          sx={{
            position: 'absolute',
            top: -12,
            left: '50%',
            transform: 'translateX(-50%)',
            fontWeight: 'bold',
            zIndex: 1
          }}
        />
      )}

      <CardActionArea
        component={RouterLink}
        to={`/products/${bundle.slug}`}
        sx={{
          p: 2,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          justifyContent: 'flex-start'
        }}
      >
        <Box sx={{ bgcolor: '#f1f5f9', borderRadius: 2, p: 1.5, mt: 1, mb: 2 }}>
          <Box
            component="img"
            src={printer.image}
            alt={`BillZapp app on a phone next to the BillZapp ${printer.name} receipt printer`}
            sx={{
              height: { xs: 180, md: 130 },
              maxWidth: '100%',
              objectFit: 'contain',
              display: 'block',
              mx: 'auto',
              // The 3 inch photo has a white background; this blends it into the grey panel.
              mixBlendMode: 'multiply'
            }}
          />
        </Box>

        <Typography variant="overline" fontWeight="bold" color="text.secondary" sx={{ fontSize: '0.75rem', lineHeight: 1.5 }}>
          {printer.name} printer
        </Typography>
        <Typography
          variant="subtitle1"
          fontWeight="bold"
          color="primary"
          // Slightly smaller while four cards share a narrow row, so the title keeps to two lines.
          sx={{ lineHeight: 1.3, mb: 1, fontSize: { md: '0.875rem', lg: '1rem' } }}
        >
          {/* Broken the same way in both cards so their prices line up. */}
          Pocket Billing Machine<br />With {software} Software
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <WasPrice paise={printer.amountPaise + planWasPaise} variant="body2" />
          <Typography variant="caption" color="error.main" fontWeight="bold">
            SAVE {formatRupees(planWasPaise - planPaise)}
          </Typography>
        </Box>
        <Typography variant="h4" fontWeight="bold" sx={{ fontSize: '1.75rem' }}>
          {formatRupees(printer.amountPaise + planPaise)}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5 }}>
          Printer + 1 year of software
        </Typography>

        <Divider />
        <Stack spacing={1} sx={{ my: 1.5, flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <CheckIcon sx={{ color: 'success.main', fontSize: 18, mt: 0.125 }} />
            <Typography variant="body2" sx={{ flex: 1 }}>{printer.name} printer</Typography>
            <Typography variant="body2" fontWeight="bold" sx={{ whiteSpace: 'nowrap' }}>
              {formatRupees(printer.amountPaise)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <CheckIcon sx={{ color: 'success.main', fontSize: 18, mt: 0.125 }} />
            <Typography variant="body2" sx={{ flex: 1 }}>{software} software</Typography>
            <Box sx={{ textAlign: 'right' }}>
              <WasPrice paise={planWasPaise} variant="caption" sx={{ display: 'block', lineHeight: 1.2 }} />
              <Typography variant="body2" fontWeight="bold" sx={{ whiteSpace: 'nowrap' }}>
                {formatRupees(planPaise)}/yr
              </Typography>
            </Box>
          </Box>
        </Stack>
        <Divider />

        <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5 }}>
          One-time printer cost. Software renews at {formatRupees(planPaise)} a year.
        </Typography>
      </CardActionArea>
    </Card>
  );
}
